import React, { ReactNode, useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, Row, Space, Typography } from "antd";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "../../Components/Layout/ContentWrapper";
import { Icon } from "@iconify/react";
import { SuccessModal } from "../../Components/SuccessModal/SuccessModal";
import { allowedDomainsAll } from "../../Utils/Constant";
import { post } from "../../../SPA/Services/api.service";
import { panelApiUrl } from "~/Utils";
import { userApi } from "../../Services";

const EqualCol = ({ children }: { children: ReactNode }) => (
  <Col
    xs={24}
    md={12}
  >
    {children}
  </Col>
);

const defaultValue = { userName: "", userEmail: "", role: "" };

export const AddPanelUser = () => {
  const navigate = useNavigate();
  const [successLoader, setSuccessLoader] = useState(false);
  const validationSchema = yup.object().shape({
    userName: yup
      .string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]+$/, "Invalid Name"),
    userEmail: yup
      .string()
      .email("Invalid email")
      .required("Email is required")
     .matches(
             new RegExp(`^[a-zA-Z0-9._%+-]+@(${allowedDomainsAll.join("|")})$`),
             `Permitted Domains: ${allowedDomainsAll.join(", ")}`
           ),
    role: yup.string().nullable().required("Role is required"),
  });
  const [loading, setLoading] = useState(false);

  const [checkMail, setCheckMail] = useState<any>();
  const [emailUnknown, setEmailUnknown] = useState(false);

  const onSubmit = (values: any) => {
    setLoading(true);
    const payload = {
      userName: values.userName,
      emailAddress: values.userEmail,
      status: "active",
      requestRoles: [values.role],
    };
    post(`${panelApiUrl}/user/add-user`, payload)
      .then((res) => {
        setSuccessLoader(true);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(-1)}
            icon="akar-icons:arrow-left"
            style={{
              fontSize: "1.8rem",
              cursor: "pointer",
            }}
          />
          <span>
            <Typography.Title
              level={3}
              style={{ margin: 0 }}
            >
              Add New User
            </Typography.Title>
          </span>
        </Space>
      }
    >
      <Formik
        initialValues={defaultValue}
        validateOnMount
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validationSchema}
      >
        {(props) => {
          useEffect(() => {
            const email = props.values.userEmail;
            if (email && props.errors) {
              const domain = email.split("@")[1];
              if (allowedDomainsAll.includes(domain)) {
                setCheckMail(email);
              }
            }
          }, [props.values.userEmail, props.errors]);

          /* Ask the backend whether the address belongs to a real directory
             user. The domain check above only proves the address *looks*
             permitted; this proves the person exists. Debounced so it does not
             fire on every keystroke, and silent on failure — the form still
             submits and the server remains the authority. */
          useEffect(() => {
            const email = props.values.userEmail;
            setEmailUnknown(false);
            if (!email || props.errors.userEmail) return;

            const timer = setTimeout(() => {
              userApi
                .isValidEmail(email)
                .then((valid: any) => setEmailUnknown(valid === false))
                .catch(() => setEmailUnknown(false));
            }, 500);
            return () => clearTimeout(timer);
          }, [props.values.userEmail, props.errors.userEmail]);

          return (
            <Form
              layout="vertical"
              onFinish={props.handleSubmit}
            >
              <Row style={{ marginTop: 16, display: "block", width: "100%" }}>
                <Col
                  xs={24}
                  md={16}
                >
                  <>
                    <Row gutter={16}>
                      <EqualCol>
                        <Form.Item required label="User Name">
                          <Field name="userName">{({ field }: FieldProps) => <Input {...field} />}</Field>
                          <Typography.Text type="danger">
                            <ErrorMessage name="userName" />
                          </Typography.Text>
                        </Form.Item>
                      </EqualCol>
                      <EqualCol>
                        <Form.Item required label="User Email">
                          <Field name="userEmail">{({ field }: FieldProps) => <Input {...field} />}</Field>
                          {emailUnknown && (
                            <Typography.Text type="danger">
                              No directory user found for this email address.
                            </Typography.Text>
                          )}
                          <Typography.Text type="danger">
                            <ErrorMessage name="userEmail" />
                          </Typography.Text>
                        </Form.Item>
                      </EqualCol>
                    </Row>
                    <Row gutter={16}>
                      <Col md={24}>
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 10 }}
                        >
                          Choose the access you want
                        </Typography.Title>
                      </Col>
                      <Col md={24}>
                        <Form.Item
                          name="role"
                          initialValue=""
                        >
                          <Field name="role">
                            {({ field }: FieldProps) => (
                              <Radio.Group {...field}>
                                <Radio value="PANEL_REVIEWER">Work Order Approver</Radio>
                                <Radio value="PANEL_ADMIN">Work Order Admin</Radio>
                              </Radio.Group>
                            )}
                          </Field>
                        </Form.Item>
                        <Typography.Text type="danger">
                          <ErrorMessage name="role" />
                        </Typography.Text>
                      </Col>
                    </Row>
                  </>
                </Col>
              </Row>
              <Row>
                <Col
                  xs={16}
                  md={16}
                  style={{ textAlign: "right" }}
                >
                  <Space>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading}
                      disabled={!props.isValid || !props.values.role || loading}
                    >
                      Submit
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
      <SuccessModal
        redirect={-1}
        title="You have successfully created a new user"
        message=""
        type="success"
        buttonText="Go Back"
        open={successLoader}
        setOpen={setSuccessLoader}
      />
    </ContentWrapper>
  );
};
